import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Wallet as WalletIcon, CheckCircle, XCircle, Clock, Eye, Plus, ArrowUpRight, ArrowDownRight, CreditCard } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminWallets() {
  const { hasPermission } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsOpen, setTransactionsOpen] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditForm, setCreditForm] = useState({
    user_id: '',
    amount: '',
    description: ''
  });
  
  // Permission checks
  const canCreditWallet = hasPermission('wallets:credit');
  const canApproveReject = hasPermission('wallets:credit'); // Approve/Reject uses the same permission

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/admin/wallets`),
        axios.get(`${API}/admin/payment-requests`)
      ]);
      setWallets(walletsRes.data);
      setPaymentRequests(requestsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };


  const viewTransactions = async (wallet) => {
    setSelectedWallet(wallet);
    try {
      const response = await axios.get(`${API}/admin/wallets/${wallet.user_id}/transactions`);
      setTransactions(response.data);
      setTransactionsOpen(true);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };

  const processRequest = async (requestId, status, adminNotes = '') => {
    setProcessing(requestId);
    try {
      await axios.put(`${API}/admin/payment-requests/${requestId}/process`, {
        status,
        admin_notes: adminNotes
      });
      toast.success(`Payment request ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setProcessing(null);
    }
  };

  const handleCreditWallet = async () => {
    if (!creditForm.amount || !creditForm.description) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.post(`${API}/admin/wallet/credit?user_id=${creditForm.user_id}&amount=${creditForm.amount}&description=${encodeURIComponent(creditForm.description)}`);
      toast.success('Wallet credited successfully');
      setCreditDialogOpen(false);
      setCreditForm({ user_id: '', amount: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to credit wallet');
    }
  };

  const openCreditDialog = (wallet) => {
    setCreditForm({
      user_id: wallet.user_id,
      amount: '',
      description: ''
    });
    setCreditDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
    if (status === 'rejected') return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
  };

  const pendingRequests = paymentRequests.filter(r => r.status === 'pending');
  const totalBalance = wallets.reduce((sum, w) => sum + (w.balance || 0), 0);

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="wallets-title">Wallet Management</h1>
          <p className="text-slate-500 mb-3">Manage reseller wallets and payment requests</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <Tabs defaultValue="requests" className="space-y-6">
            <TabsList>
              <TabsTrigger value="requests" className="relative">
                Payment Requests
                {pendingRequests.length > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                    {pendingRequests.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="wallets">All Wallets</TabsTrigger>
            </TabsList>

            {/* Payment Requests Tab */}
            <TabsContent value="requests" className="space-y-4">
              {paymentRequests.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <CreditCard className="h-12 w-12 text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium text-slate-700">No payment requests</h3>
                    <p className="text-slate-500 text-sm mt-1">Payment requests will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4 grid gap-4 overflow-x-auto">
                  {paymentRequests.map(request => (
                    <Card key={request.id} data-testid={`payment-request-${request.id}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between flex-wrap md:flex-nowrap">
                          <div className="space-y-2">
                            <div className="flex items-center gap-3">
                              <p className="text-2xl font-bold text-blue-600 mono">₹{(request.amount || 0).toLocaleString()}</p>
                              {getStatusBadge(request.status)}
                            </div>
                            <div className="text-sm text-slate-600 space-y-1">
                              <p><span className="text-slate-500">From:</span> {request.user?.company_name || request.user?.email}</p>
                              <p><span className="text-slate-500">UTR:</span> <span className="font-mono">{request.utr_number}</span></p>
                              <p><span className="text-slate-500">Payment Date:</span> {request.payment_date}</p>
                              {request.notes && <p><span className="text-slate-500">Notes:</span> {request.notes}</p>}
                              <p className="text-xs text-slate-400">Submitted: {new Date(request.created_at).toLocaleString()}</p>
                            </div>
                          </div>
                          {request.status === 'pending' && canApproveReject && (
                            <div className="flex gap-2 w-full sm:w-auto mt-3 sm:mt-0">
                              <Button
                                size="sm"
                                onClick={() => processRequest(request.id, 'approved')}
                                disabled={processing === request.id}
                                data-testid={`approve-${request.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const notes = window.prompt('Reason for rejection (optional):');
                                  processRequest(request.id, 'rejected', notes || '');
                                }}
                                disabled={processing === request.id}
                                data-testid={`reject-${request.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                          {request.status !== 'pending' && request.processed_at && (
                            <div className="text-right text-sm text-slate-500 w-full sm:w-auto mt-3 sm:mt-0">
                              {request.processed_at && <p>Processed: {new Date(request.processed_at).toLocaleString()}</p>}
                              {request.admin_notes && <p className="text-xs mt-1">Note: {request.admin_notes}</p>}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* All Wallets Tab */}
            <TabsContent value="wallets" className="space-y-4">
              {/* Summary Card */}
              <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Total Reseller Balance</p>
                      <p className="text-4xl font-bold mt-1 mono">₹{(totalBalance || 0).toLocaleString()}</p>
                      <p className="text-green-200 text-sm mt-2">{wallets.length} wallets</p>
                    </div>
                    <WalletIcon className="h-16 w-16 text-green-300 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              {/* Wallets List */}
              <div className="grid gap-4 overflow-x-auto">
                {wallets.map(wallet => (
                  <Card key={wallet.id} data-testid={`wallet-${wallet.user_id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{wallet.user?.company_name || 'N/A'}</h3>
                            <Badge variant="outline">{wallet.user?.role}</Badge>
                          </div>
                          <p className="text-sm text-slate-500">{wallet.user?.email}</p>
                          <p className="text-xs text-slate-400">Last updated: {new Date(wallet.updated_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-2xl font-bold text-blue-600 mono">₹{(wallet.balance || 0).toLocaleString()}</p>
                            <p className="text-xs text-slate-500">Current Balance</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => viewTransactions(wallet)}
                              data-testid={`view-transactions-${wallet.user_id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {canCreditWallet && (
                              <Button
                                size="sm"
                                onClick={() => openCreditDialog(wallet)}
                                data-testid={`credit-${wallet.user_id}`}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Transactions Dialog */}
        <Dialog open={transactionsOpen} onOpenChange={setTransactionsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Transaction History - {selectedWallet?.user?.company_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-4">
              {transactions.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No transactions found</p>
              ) : (
                transactions.map(txn => (
                  <div 
                    key={txn.id} 
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${txn.type === 'credit' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {txn.type === 'credit' ? (
                          <ArrowDownRight className="h-4 w-4 text-green-600" />
                        ) : (
                          <ArrowUpRight className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{txn.description}</p>
                        <p className="text-xs text-slate-500">{new Date(txn.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold mono ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                        {txn.type === 'credit' ? '+' : ''}₹{Math.abs(txn.amount || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500">Bal: ₹{(txn.balance_after || 0).toLocaleString()}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTransactionsOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Credit Wallet Dialog */}
        <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Credit Wallet</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={creditForm.amount}
                  onChange={(e) => setCreditForm(prev => ({...prev, amount: e.target.value}))}
                  data-testid="credit-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Reason for crediting..."
                  value={creditForm.description}
                  onChange={(e) => setCreditForm(prev => ({...prev, description: e.target.value}))}
                  data-testid="credit-description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreditWallet} data-testid="submit-credit-btn">Credit Wallet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
}
